// EmbedShare.jsx - Generates copy-paste embed code for booking page
// 4 types: Direct Link / Full Page Iframe / Button Modal / Floating Bubble
// Per-service mode: pick a service → every embed deep-links straight to
// that service's date/provider step (/book?service=<id>), and the button/
// bubble text auto-names the service so clients know what they're booking.

import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const BOOK_URL = "https://www.elyonhq.com/book";

export default function EmbedShare() {
  const [type, setType] = useState("link");
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState(""); // "" = whole booking page
  const [config, setConfig] = useState({
    buttonText: "Book Now",
    buttonColor: "#b48c3c",
    bubblePosition: "bottom-right",
    bubbleText: "📅 Book Appointment",
    iframeHeight: 800,
  });

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("services")
        .select("id,name,category,price").eq("active", true)
        .order("category").order("name");
      setServices((data || []).filter(s => !/^retail/i.test(String(s.category || "").trim())));
    })();
  }, []);

  const update = (k, v) => setConfig(c => ({...c, [k]: v}));

  // serviceId can be a service id OR "cat:<Category Name>" for a whole-category embed.
  const isCat = String(serviceId).startsWith("cat:");
  const catName = isCat ? String(serviceId).slice(4) : null;
  const selectedService = !isCat ? (services.find(s => String(s.id) === String(serviceId)) || null) : null;
  const targetLabel = catName || selectedService?.name || null;
  // The URL every embed points at — whole page, a single service, or a category.
  const bookUrl = isCat
    ? `${BOOK_URL}?category=${encodeURIComponent(catName)}`
    : selectedService ? `${BOOK_URL}?service=${selectedService.id}` : BOOK_URL;
  // Default the button/bubble text to the target name so clients see what
  // they're booking, but only auto-fill when the owner hasn't customized it.
  const ctaText = targetLabel ? `Book ${targetLabel}` : config.buttonText;
  const bubbleText = targetLabel ? `📅 Book ${targetLabel}` : config.bubbleText;
  const ctx = { ...config, bookUrl, ctaText, bubbleText, serviceName: targetLabel };
  const categories = [...new Set(services.map(s => s.category).filter(Boolean))];

  const types = [
    { id: "link",   icon: "🔗", title: "Direct Link",      desc: "Share this URL anywhere - email, social, SMS" },
    { id: "iframe", icon: "📄", title: "Full Page Embed",  desc: "Drop full booking page into your website" },
    { id: "button", icon: "🟡", title: "Button + Modal",   desc: "A 'Book Now' button that opens a popup" },
    { id: "bubble", icon: "💬", title: "Floating Bubble",  desc: "A floating chat-style bubble in the corner" },
  ];

  return (
    <div>
      <div style={{marginBottom:18}}>
        <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Embed & Share</div>
        <div style={{fontSize:11,color:C.muted}}>Get your booking page anywhere — your website (novamedicalspa.com), social, ads</div>
      </div>

      {/* ★ PER-SERVICE SELECTOR — pick a treatment to generate a deep-link
          embed that drops the client straight onto that service's date
          picker, with the service name shown on the button. */}
      <div style={{padding:14,background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:10,marginBottom:18}}>
        <Label>What should this embed book?</Label>
        <select value={serviceId} onChange={e=>setServiceId(e.target.value)} style={inputStyle}>
          <option value="">— Full menu (client picks any service) —</option>
          <optgroup label="── Whole Category ──">
            {categories.map(c => <option key={"cat:"+c} value={"cat:"+c}>📂 {c} (all)</option>)}
          </optgroup>
          {Object.entries(
            services.reduce((acc, s) => { (acc[s.category||"Other"] ||= []).push(s); return acc; }, {})
          ).map(([cat, list]) => (
            <optgroup key={cat} label={cat}>
              {list.map(s => <option key={s.id} value={s.id}>{s.name}{s.price?` — $${s.price}`:""}</option>)}
            </optgroup>
          ))}
        </select>
        <div style={{fontSize:11,color:C.muted,marginTop:8}}>
          {isCat
            ? `✨ This embed opens the booking page showing only the "${catName}" treatments.`
            : selectedService
            ? `✨ This embed jumps clients straight to "${selectedService.name}" — they only pick a time. No menu navigation.`
            : "Showing the full booking menu. Pick a category or a specific service above to make a focused embed."}
        </div>
      </div>

      {/* Type picker */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:10,marginBottom:20}}>
        {types.map(t => (
          <button key={t.id} onClick={()=>setType(t.id)} style={{
            padding:"14px",
            background: type === t.id ? C.goldBg : C.card,
            border: "2px solid " + (type === t.id ? C.gold : C.border),
            borderRadius: 10,
            cursor: "pointer",
            textAlign: "left",
            fontFamily: F,
          }}>
            <div style={{fontSize:24,marginBottom:6}}>{t.icon}</div>
            <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3}}>{t.title}</div>
            <div style={{fontSize:11,color:C.muted}}>{t.desc}</div>
          </button>
        ))}
      </div>

      {/* Type-specific UI */}
      {type === "link" && <DirectLink ctx={ctx}/>}
      {type === "iframe" && <IframeEmbed config={ctx} update={update}/>}
      {type === "button" && <ButtonModal config={ctx} update={update}/>}
      {type === "bubble" && <FloatingBubble config={ctx} update={update}/>}
    </div>
  );
}

// ────────────────────────────────────────
function DirectLink({ ctx }) {
  return (
    <Card title="Direct Booking Link" desc={ctx.serviceName ? `Links straight to "${ctx.serviceName}" — client just picks a time` : "Share this URL anywhere clients can click"}>
      <CopyBox value={ctx.bookUrl}/>
      <div style={{marginTop:12,padding:12,background:C.greenL,border:"1px solid "+C.green+"30",borderRadius:8,fontSize:12,color:C.green}}>
        💡 <strong>Pro tip:</strong> Add this link to your Instagram bio, Google Business profile, email signatures, and SMS templates. Use a UTM parameter to track which channel sends most bookings (e.g. <code style={{background:"rgba(255,255,255,0.5)",padding:"1px 4px",borderRadius:3}}>?utm_source=instagram</code>).
      </div>
    </Card>
  );
}

// ────────────────────────────────────────
function IframeEmbed({ config, update }) {
  const code = `<iframe
  src="${config.bookUrl}"
  width="100%"
  height="${config.iframeHeight}"
  frameborder="0"
  style="border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.08);max-width:100%;"
  title="${config.serviceName ? "Book " + config.serviceName : "Book your appointment"}">
</iframe>`;

  return (
    <Card title="Full Page Embed" desc={config.serviceName ? `Embeds the "${config.serviceName}" booking flow directly in your page` : "Drop this into your website to show the full booking page inline"}>
      <div style={{display:"grid",gridTemplateColumns:"1fr",gap:10,marginBottom:14}}>
        <div>
          <Label>Iframe Height (px)</Label>
          <input type="number" value={config.iframeHeight} onChange={e=>update("iframeHeight", +e.target.value)} style={inputStyle}/>
        </div>
      </div>
      <CopyBox value={code} multiline/>
    </Card>
  );
}

// ────────────────────────────────────────
function ButtonModal({ config, update }) {
  const btnLabel = config.serviceName ? config.ctaText : config.buttonText;
  const code = `<!-- Place this where you want the button -->
<button
  onclick="document.getElementById('elyon-booking-modal').style.display='flex'"
  style="background:${config.buttonColor};color:#fff;padding:14px 28px;border:none;border-radius:10px;font-size:15px;font-weight:700;cursor:pointer;box-shadow:0 4px 16px rgba(0,0,0,0.15);">
  ${btnLabel}
</button>

<!-- Place this once at the end of your <body> -->
<div id="elyon-booking-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:9999;align-items:center;justify-content:center;padding:20px;">
  <div style="background:#fff;border-radius:14px;width:100%;max-width:900px;height:90vh;position:relative;overflow:hidden;">
    <button onclick="document.getElementById('elyon-booking-modal').style.display='none'"
      style="position:absolute;top:12px;right:12px;width:36px;height:36px;background:rgba(0,0,0,0.7);color:#fff;border:none;border-radius:50%;font-size:18px;cursor:pointer;z-index:10;">×</button>
    <iframe src="${config.bookUrl}" width="100%" height="100%" frameborder="0" title="Book"></iframe>
  </div>
</div>`;

  return (
    <Card title="Button + Popup Modal" desc="A button that opens the booking page in a popup. Drives higher conversion than navigating away.">
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
        <div>
          <Label>Button Text {config.serviceName && "(auto-named for the service)"}</Label>
          <input value={btnLabel} onChange={e=>update("buttonText", e.target.value)} style={inputStyle}/>
        </div>
        <div>
          <Label>Button Color</Label>
          <input type="color" value={config.buttonColor} onChange={e=>update("buttonColor", e.target.value)} style={{width:"100%",height:38,border:"1px solid "+C.border,borderRadius:7,cursor:"pointer",padding:2}}/>
        </div>
      </div>

      {/* Live preview */}
      <Label>Preview</Label>
      <div style={{padding:24,background:C.light,borderRadius:10,marginBottom:14,textAlign:"center"}}>
        <button style={{background:config.buttonColor,color:"#fff",padding:"14px 28px",border:"none",borderRadius:10,fontSize:15,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 16px rgba(0,0,0,0.15)"}}>
          {btnLabel}
        </button>
      </div>

      <CopyBox value={code} multiline/>
    </Card>
  );
}

// ────────────────────────────────────────
function FloatingBubble({ config, update }) {
  const positions = {
    "bottom-right": "bottom:24px;right:24px;",
    "bottom-left":  "bottom:24px;left:24px;",
    "top-right":    "top:24px;right:24px;",
    "top-left":     "top:24px;left:24px;",
  };

  const bubLabel = config.serviceName ? config.bubbleText : config.bubbleText;
  const code = `<!-- Floating booking bubble - paste before </body> -->
<div id="elyon-bubble" style="position:fixed;${positions[config.bubblePosition]}z-index:9999;">
  <button
    onclick="document.getElementById('elyon-booking-popup').style.display='flex'"
    style="background:linear-gradient(135deg,${config.buttonColor},${darken(config.buttonColor)});color:#fff;padding:14px 22px;border:none;border-radius:50px;font-size:14px;font-weight:700;cursor:pointer;box-shadow:0 6px 20px rgba(0,0,0,0.2);display:flex;align-items:center;gap:8px;font-family:Arial,sans-serif;animation:elyon-pulse 2s infinite;">
    ${bubLabel}
  </button>
</div>

<style>
  @keyframes elyon-pulse {
    0%,100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  #elyon-bubble button:hover { transform: scale(1.08) !important; }
</style>

<div id="elyon-booking-popup" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:9999;align-items:center;justify-content:center;padding:20px;">
  <div style="background:#fff;border-radius:14px;width:100%;max-width:900px;height:90vh;position:relative;overflow:hidden;">
    <button onclick="document.getElementById('elyon-booking-popup').style.display='none'"
      style="position:absolute;top:12px;right:12px;width:36px;height:36px;background:rgba(0,0,0,0.7);color:#fff;border:none;border-radius:50%;font-size:18px;cursor:pointer;z-index:10;">×</button>
    <iframe src="${config.bookUrl}" width="100%" height="100%" frameborder="0" title="Book"></iframe>
  </div>
</div>`;

  return (
    <Card title="Floating Bubble" desc="A small floating button that follows scroll. Highest conversion - always visible.">
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
        <div>
          <Label>Bubble Text {config.serviceName && "(auto-named)"}</Label>
          <input value={bubLabel} onChange={e=>update("bubbleText", e.target.value)} style={inputStyle}/>
        </div>
        <div>
          <Label>Color</Label>
          <input type="color" value={config.buttonColor} onChange={e=>update("buttonColor", e.target.value)} style={{width:"100%",height:38,border:"1px solid "+C.border,borderRadius:7,cursor:"pointer",padding:2}}/>
        </div>
        <div>
          <Label>Position</Label>
          <select value={config.bubblePosition} onChange={e=>update("bubblePosition", e.target.value)} style={inputStyle}>
            <option value="bottom-right">Bottom Right</option>
            <option value="bottom-left">Bottom Left</option>
            <option value="top-right">Top Right</option>
            <option value="top-left">Top Left</option>
          </select>
        </div>
      </div>

      {/* Preview */}
      <Label>Preview</Label>
      <div style={{position:"relative",height:200,background:C.light,borderRadius:10,marginBottom:14,overflow:"hidden"}}>
        <div style={{fontSize:11,color:C.dim,padding:14,fontStyle:"italic"}}>(this is what your website would look like with the bubble)</div>
        <div style={{position:"absolute",[config.bubblePosition.includes("bottom")?"bottom":"top"]:16,[config.bubblePosition.includes("right")?"right":"left"]:16}}>
          <button style={{background:`linear-gradient(135deg,${config.buttonColor},${darken(config.buttonColor)})`,color:"#fff",padding:"14px 22px",border:"none",borderRadius:50,fontSize:14,fontWeight:700,boxShadow:"0 6px 20px rgba(0,0,0,0.2)",display:"flex",alignItems:"center",gap:8}}>
            {bubLabel}
          </button>
        </div>
      </div>

      <CopyBox value={code} multiline/>
    </Card>
  );
}

// Darken hex color helper
function darken(hex) {
  const num = parseInt(hex.replace("#",""), 16);
  const amt = -30;
  const R = Math.max(0, (num >> 16) + amt);
  const G = Math.max(0, ((num >> 8) & 0xff) + amt);
  const B = Math.max(0, (num & 0xff) + amt);
  return "#" + ((R << 16) | (G << 8) | B).toString(16).padStart(6, "0");
}

// ────────────────────────────────────────
const inputStyle = {width:"100%",padding:"9px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

function Label({ children }) {
  return <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5,fontFamily:F}}>{children}</label>;
}

function Card({ title, desc, children }) {
  return (
    <div style={{padding:18,background:C.card,border:"1px solid "+C.border,borderRadius:10}}>
      <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:3,fontFamily:F}}>{title}</div>
      <div style={{fontSize:11,color:C.muted,marginBottom:16}}>{desc}</div>
      {children}
    </div>
  );
}

function CopyBox({ value, multiline }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div style={{position:"relative"}}>
      {multiline ? (
        <textarea value={value} readOnly rows={Math.min(15, value.split("\n").length+1)} style={{...inputStyle,fontFamily:"monospace",fontSize:11,resize:"vertical",paddingRight:90}}/>
      ) : (
        <input value={value} readOnly style={{...inputStyle,fontFamily:"monospace",fontSize:13,paddingRight:90}}/>
      )}
      <button onClick={copy} style={{position:"absolute",top:6,right:6,padding:"7px 14px",background:copied?C.green:C.gold,color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>
        {copied?"✓ Copied!":"📋 Copy"}
      </button>
    </div>
  );
}
